/**
 * Rating and scale questions (#50).
 *
 * A scale is an ordered option set, which is what makes it the cheapest new
 * question type: the vote lock is a conditional write keyed on `optionId`, so
 * `claimVote`, the `COUNT#` items and dedupe are all untouched. What a scale
 * adds is that its options are *generated* from a preset rather than authored,
 * so the stored question carries the preset and every reader regenerates the
 * same points from it.
 *
 * The preset is stored, not the bounds, so there is one spelling of "1 to 5"
 * rather than a min and a max free to disagree with each other and with the
 * option list they are supposed to describe.
 */

export type ScalePreset = 'oneToFive' | 'oneToTen' | 'npsZeroToTen';

export interface ScaleShape {
  /** The lowest point, which is 0 only for NPS. */
  readonly min: number;
  readonly max: number;
  /** Author-facing name, in the builder's answer-type picker. */
  readonly label: string;
  /** What the ends mean, printed under the scale on the phone. */
  readonly minLabel: string;
  readonly maxLabel: string;
}

export const SCALE_PRESETS: Record<ScalePreset, ScaleShape> = {
  oneToFive: {
    min: 1,
    max: 5,
    label: 'Rating 1–5',
    minLabel: 'Poor',
    maxLabel: 'Excellent',
  },
  oneToTen: {
    min: 1,
    max: 10,
    label: 'Rating 1–10',
    minLabel: 'Poor',
    maxLabel: 'Excellent',
  },
  npsZeroToTen: {
    min: 0,
    max: 10,
    // The standard NPS anchors, which are the question rather than decoration:
    // a 0-10 scale read as a rating out of ten is a different question.
    label: 'NPS 0–10',
    minLabel: 'Not at all likely',
    maxLabel: 'Extremely likely',
  },
};

export function isScalePreset(value: unknown): value is ScalePreset {
  return typeof value === 'string' && Object.hasOwn(SCALE_PRESETS, value);
}

/** The points of a scale, ascending — the option set, in order. */
export function scalePoints(preset: ScalePreset): number[] {
  const { min, max } = SCALE_PRESETS[preset];
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

/**
 * The mean rating, or null when nobody has rated yet.
 *
 * Counts are positional — the nth count belongs to the nth point — which is the
 * order `buildQuestionResults` projects options in, and the order
 * `validateScreens` guarantees they are stored in. Deliberately not derived by
 * parsing `option.text`: the text is what the point *displays*, and reading a
 * number back out of it would put a second, weaker rule beside the one the
 * validator already enforces.
 *
 * One decimal place, because a mean of ratings is not more precise than that
 * and a projector prints it at reading distance.
 */
export function scaleAverage(counts: readonly number[], preset: ScalePreset): number | null {
  const { min } = SCALE_PRESETS[preset];
  let weighted = 0;
  let rated = 0;

  counts.forEach((count, index) => {
    weighted += (min + index) * count;
    rated += count;
  });

  return rated === 0 ? null : Math.round((weighted / rated) * 10) / 10;
}
